import { useState } from "react";
import { api, type Session, type Today } from "../api.ts";
import { captionStyle, colors, serif, tokens } from "../theme.ts";
import { GlassButton, GlassCard, inputStyle } from "../components/glass.tsx";

/**
 * Evening: one text field, "What happened". Submitting saves what was typed
 * as a log note and records the sitting. There is no assistant in V2, so
 * nothing interprets the text, replies to it or changes a task; the log is
 * append-only, so there is no undo either. Reporting nothing is a valid
 * evening: the field can be left empty and the screen does not comment.
 */
export function Evening({
  data, doneToday, onOpen, onWritten,
}: { data: Today; doneToday: Session | null; onOpen: (id: string) => void; onWritten: () => void }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const said = text.trim();
    setBusy(true); setError(null);
    try {
      if (said) await api.log({ kind: "note", text: said });
      try { await api.recordSession({ kind: "evening", transcript: said || null, wrote: said ? [{ logged: "note" }] : [] }); }
      catch { /* the note is saved; only the record of the sitting failed */ }
      setSaved(true);
      onWritten();
    } catch (e: any) { setError(e?.message ?? "that did not go through"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      {!data.travel && data.heavy && (
        <GlassCard padding={16} style={{ cursor: "pointer", marginBottom: 16 }} onClick={() => onOpen(data.heavy!.id)}>
          <div style={captionStyle}>{data.heavy.breadcrumb}</div>
          <div style={{
            fontFamily: serif, fontSize: tokens.type.heavyTitle.size, lineHeight: tokens.type.heavyTitle.lineHeight,
            letterSpacing: tokens.type.heavyTitle.letterSpacing, margin: "6px 0 0", color: colors.ink,
          }}>{data.heavy.title}</div>
        </GlassCard>
      )}

      <GlassCard padding={16}>
        {doneToday || saved ? (
          <p style={{ margin: 0, fontSize: 14, color: colors.ink }}>Recorded today.</p>
        ) : (
          <div>
            <textarea
              value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="What happened"
              style={{ ...inputStyle, resize: "none" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <GlassButton variant="primary" disabled={busy} onClick={submit}>{busy ? "…" : "Submit"}</GlassButton>
            </div>
            {error && <p style={{ margin: "8px 0 0", fontSize: 12.5, color: colors.ink }}>{error}</p>}
          </div>
        )}
      </GlassCard>
    </div>
  );
}
